// Landing page for a password-recovery link.
//
// Two link shapes arrive here:
//
//   1. Ours (what admin-users issues now):
//        /reset-password?token_hash=…&type=recovery
//      The token is NOT consumed on page load. GoTrue tokens are single-use,
//      and WhatsApp / Telegram / Slack fetch every link they display to build
//      a preview — so a link that verified itself on GET was "expired" before
//      the recipient ever tapped it. That is the whole reason this page shows
//      a button first: only a person pressing it calls verifyOtp.
//
//   2. GoTrue's own action_link (older links, or SMTP-delivered mail):
//        /reset-password#access_token=…&refresh_token=…&type=recovery
//      The API has already verified the token and redirected here with the
//      session in the fragment.
//
// The supabase client is created with `detectSessionInUrl: false` (see
// lib/supabase.ts), so nothing picks either up on its own — deliberately,
// since enabling it globally would have every page consume URL fragments.

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

type Phase = 'checking' | 'confirm' | 'ready' | 'invalid' | 'done';

/** Errors arrive in the fragment too, e.g. #error=access_denied&error_description=… */
function readFragment(): Record<string, string> {
  const raw = window.location.hash.replace(/^#/, '');
  const out: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(raw)) out[k] = v;
  return out;
}

const expiredMessage = 'Link reset sudah kedaluwarsa atau sudah pernah dipakai. Minta admin membuat link baru.';

function describeAuthError(message: string, code?: string): string {
  return code === 'otp_expired' || /expired|invalid/i.test(message) ? expiredMessage : message;
}

/** Strip tokens out of the address bar once the client holds them. */
const scrubUrl = () => window.history.replaceState({}, '', window.location.pathname);

export default function ResetPassword() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('checking');
  const [reason, setReason] = useState<string | null>(null);
  const [tokenHash, setTokenHash] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Shape 1: hashed token in the query. Park it and wait for the button.
    const q = new URLSearchParams(window.location.search);
    const th = q.get('token_hash');
    if (th && q.get('type') === 'recovery') {
      setTokenHash(th);
      setPhase('confirm');
      return;
    }

    // Shape 2: session in the fragment.
    const f = readFragment();
    if (f.error || f.error_description) {
      setReason(describeAuthError(f.error_description ?? 'Link tidak valid.', f.error_code));
      setPhase('invalid');
      return;
    }
    if (!f.access_token || !f.refresh_token) {
      setReason('Link tidak lengkap. Buka link reset password langsung dari pesan yang dikirim admin.');
      setPhase('invalid');
      return;
    }
    supabase.auth
      .setSession({ access_token: f.access_token, refresh_token: f.refresh_token })
      .then(({ error: e }) => {
        if (e) {
          setReason(describeAuthError(e.message));
          setPhase('invalid');
          return;
        }
        scrubUrl();
        setPhase('ready');
      });
  }, []);

  const confirmLink = async () => {
    if (!tokenHash) return;
    setVerifying(true);
    const { error: e } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'recovery' });
    setVerifying(false);
    if (e) {
      setReason(describeAuthError(e.message, (e as { code?: string }).code));
      setPhase('invalid');
      return;
    }
    scrubUrl();
    setPhase('ready');
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw.length < 8) return setError('Password minimal 8 karakter.');
    if (pw !== pw2) return setError('Konfirmasi password tidak sama.');

    setSaving(true);
    const { error: e1 } = await supabase.auth.updateUser({ password: pw });
    setSaving(false);
    if (e1) return setError(e1.message);

    // Sign out so the next step is a real login with the new password —
    // proof it works, rather than landing in a session they cannot reproduce.
    await supabase.auth.signOut();
    setPhase('done');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex w-12 h-12 rounded-lg bg-primary/10 text-primary items-center justify-center mb-3">
            <KeyRound size={22} />
          </div>
          <h1 className="text-section font-display font-semibold text-ink">Atur ulang password</h1>
        </div>

        <div className="bg-surface border border-line rounded-lg p-6">
          {phase === 'checking' && (
            <p className="text-small text-ink-subtle text-center">Memeriksa link…</p>
          )}

          {phase === 'confirm' && (
            <div className="text-center space-y-4">
              <p className="text-small text-ink">
                Tekan tombol di bawah untuk melanjutkan mengatur ulang password.
              </p>
              <Button variant="primary" fullWidth onClick={confirmLink} loading={verifying}>
                Lanjutkan
              </Button>
              <p className="text-caption text-ink-subtle">
                Link ini hanya bisa dipakai satu kali.
              </p>
            </div>
          )}

          {phase === 'invalid' && (
            <div className="text-center space-y-4">
              <AlertTriangle size={28} className="mx-auto text-danger" />
              <p className="text-small text-ink">{reason}</p>
              <Button variant="secondary" fullWidth onClick={() => navigate('/login')}>
                Kembali ke login
              </Button>
            </div>
          )}

          {phase === 'done' && (
            <div className="text-center space-y-4">
              <CheckCircle2 size={28} className="mx-auto text-success" />
              <p className="text-small text-ink">
                Password berhasil diubah. Silakan masuk dengan password baru.
              </p>
              <Button variant="primary" fullWidth onClick={() => navigate('/login')}>
                Masuk sekarang
              </Button>
            </div>
          )}

          {phase === 'ready' && (
            <form onSubmit={submit} className="space-y-4">
              <Input
                label="Password baru"
                type="password"
                autoComplete="new-password"
                autoFocus
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                hint="Minimal 8 karakter."
              />
              <Input
                label="Ulangi password baru"
                type="password"
                autoComplete="new-password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
              />
              {error && (
                <p role="alert" className="text-caption text-danger">{error}</p>
              )}
              <Button type="submit" variant="primary" fullWidth loading={saving}>
                Simpan password baru
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
